/**
 * What the browser's live catalogue and the server's copy of it must agree on.
 *
 * The marketplace renders its first screen on the server (see
 * src/lib/marketplace-first-screen.ts) and then hands over to the live
 * listener in src/context/product-context.tsx. The two read the same slice of
 * the same collection and read the aggregates under the same names, or the grid
 * reshuffles at the handover for reasons nobody could see.
 *
 * A module of its own because product-context.tsx is a client module: a server
 * module importing from it gets a client reference, not the value, so neither
 * the cap nor the helper below could be shared by importing them from there.
 */

/**
 * Hard ceiling on the "full catalogue" listener the marketplace mounts, and so
 * on the slice the server's first screen ranks. The reasoning for the cap is at
 * its use in product-context.tsx.
 */
export const CATALOG_MAX_DOCS = 1000;

/**
 * A product document's aggregates under the names the UI reads.
 *
 * Cloud Functions write `avgRating`, `totalReviews`, `totalSales` and
 * `totalRevenue`; older client code wrote `rating`, `reviewCount`, `sales` and
 * `revenue`, and the create form still initialises those four to 0 and never
 * touches them again. See normalizeProductDoc in product-context.tsx, which is
 * built on this.
 *
 * @param data The document's raw fields.
 */
export function catalogAggregates(data: Record<string, any>): {
  rating: number;
  reviewCount: number;
  sales: number;
  revenue: number;
} {
  return {
    rating: data.avgRating ?? data.rating ?? 0,
    reviewCount: data.totalReviews ?? data.reviewCount ?? 0,
    sales: data.totalSales ?? data.sales ?? 0,
    revenue: data.totalRevenue ?? data.revenue ?? 0,
  };
}
